import { Navigate, Route, Routes, useLocation } from "react-router";
import { authClient } from "../api";
import { ConnectPage } from "../features/featstudio";
import { BillingPage, InvoicesPage } from "../features/billing";
import { CachePage } from "../features/cache";
import { AtAGlancePage } from "../features/at-a-glance";
import {
  BranchOrTagBuildsPage,
  BuildDetailPage,
  BuildsPage,
  EnvironmentTimelinePage,
  ReleaseRulesPage,
  ReleaseNotesPage,
} from "../features/builds";
import { GitHubPage } from "../features/github";
import { EngineeringHealthPage } from "../features/engineering-health";
import { InvolvementPage } from "../features/involvement";
import { DecisionInboxPage } from "../features/decision-inbox";
import { DocumentationPage } from "../features/documentation";
import {
  ForgotPasswordPage,
  LoginPage,
  ResetPasswordPage,
} from "../features/auth";
import { InvitationLoginPage, InvitationPage } from "../features/invitations";
import { ProfilePage } from "../features/profile";
import { SecurityPage } from "../features/security";
import { TerrabuildIntegrationPage } from "../features/terrabuild-integration";
import { GeneralPage, TeamsPage } from "../features/workspace-administration";
import {
  CheckoutCompletePage,
  CreateWorkspacePage,
  HomePage,
} from "../features/workspaces";
import {
  Loading,
  OwnerOnlyRoute,
  WorkspaceFeatureRoute,
} from "../shared/components";

export function App() {
  const session = authClient.useSession();
  const location = useLocation();
  const backgroundLocation =
    typeof location.state === "object" &&
    location.state !== null &&
    "buildModalBackground" in location.state &&
    typeof location.state.buildModalBackground === "object" &&
    location.state.buildModalBackground !== null
      ? location.state.buildModalBackground
      : undefined;

  if (session.isPending) {
    return <Loading />;
  }
  if (
    location.pathname === "/forgot-password" ||
    location.pathname === "/reset-password"
  ) {
    return (
      <Routes>
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Routes>
    );
  }
  if (!session.data) {
    return (
      <Routes>
        <Route path="/connect/featstudio" element={<LoginPage returnTo={location.pathname + location.search} />} />
        <Route path="/invitations/:id" element={<InvitationLoginPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }
  return (
    <>
      <Routes location={backgroundLocation ?? location}>
        <Route path="/connect/featstudio" element={<ConnectPage />} />
        <Route path="/" element={<HomePage />} />
        <Route path="/workspaces/new" element={<CreateWorkspacePage />} />
        <Route
          path="/billing/checkout-complete"
          element={<CheckoutCompletePage />}
        />
        <Route path="/invitations/:id" element={<InvitationPage />} />
        <Route
          path="/w/:workspaceId/pulse/activity"
          element={<AtAGlancePage />}
        />
        <Route path="/w/:workspaceId/builds" element={<BuildsPage />} />
        <Route
          path="/w/:workspaceId/builds/branch-or-tag"
          element={<BranchOrTagBuildsPage />}
        />
        <Route
          path="/w/:workspaceId/builds/timeline"
          element={<EnvironmentTimelinePage />}
        />
        <Route
          path="/w/:workspaceId/builds/release-notes"
          element={
            <Navigate
              to={`${location.pathname.replace(/release-notes$/, "comparison")}${location.search}`}
              replace
            />
          }
        />
        <Route
          path="/w/:workspaceId/builds/comparison"
          element={<ReleaseNotesPage />}
        />
        <Route
          path="/w/:workspaceId/builds/:buildId"
          element={<BuildDetailPage />}
        />
        <Route path="/w/:workspaceId/github" element={<GitHubPage />} />
        <Route path="/w/:workspaceId/pulse" element={<Navigate to="inbox" replace />} />
        <Route
          path="/w/:workspaceId/pulse/inbox"
          element={
            <WorkspaceFeatureRoute feature="decisionInbox">
              <DecisionInboxPage />
            </WorkspaceFeatureRoute>
          }
        />
        <Route path="/w/:workspaceId/pulse/flow" element={<EngineeringHealthPage section="flow" />} />
        <Route path="/w/:workspaceId/pulse/feedback" element={<EngineeringHealthPage section="feedback" />} />
        <Route path="/w/:workspaceId/pulse/delivery" element={<EngineeringHealthPage section="delivery" />} />
        <Route path="/w/:workspaceId/pulse/behavior" element={<EngineeringHealthPage section="behavior" />} />
        <Route path="/w/:workspaceId/pulse/involvement" element={<InvolvementPage />} />
        <Route path="/w/:workspaceId/teams" element={<TeamsPage />} />
        <Route path="/w/:workspaceId/cache" element={<CachePage />} />
        <Route
          path="/w/:workspaceId/general"
          element={<GeneralPage />}
        />
        <Route
          path="/w/:workspaceId/billing"
          element={
            <OwnerOnlyRoute>
              <BillingPage />
            </OwnerOnlyRoute>
          }
        />
        <Route
          path="/w/:workspaceId/invoices"
          element={
            <OwnerOnlyRoute>
              <InvoicesPage />
            </OwnerOnlyRoute>
          }
        />
        <Route path="/w/:workspaceId/profile" element={<ProfilePage />} />
        <Route path="/w/:workspaceId/security" element={<SecurityPage />} />
        <Route
          path="/w/:workspaceId/release-rules"
          element={<ReleaseRulesPage />}
        />
        <Route
          path="/w/:workspaceId/docs"
          element={<Navigate to="overview" replace />}
        />
        <Route
          path="/w/:workspaceId/docs/overview"
          element={<DocumentationPage chapter="overview" />}
        />
        <Route
          path="/w/:workspaceId/docs/connect"
          element={<DocumentationPage chapter="connect" />}
        />
        <Route
          path="/w/:workspaceId/docs/builds"
          element={<DocumentationPage chapter="builds" />}
        />
        <Route
          path="/w/:workspaceId/docs/pulse"
          element={<DocumentationPage chapter="pulse" />}
        />
        <Route
          path="/w/:workspaceId/docs/workspace"
          element={<DocumentationPage chapter="workspace" />}
        />
        <Route
          path="/w/:workspaceId/integration/terrabuild"
          element={<TerrabuildIntegrationPage />}
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>

      {backgroundLocation && (
        <Routes>
          <Route
            path="/w/:workspaceId/builds/:buildId"
            element={<BuildDetailPage overlay />}
          />
        </Routes>
      )}
    </>
  );
}
